import type { DecisionToolProps, Defect } from "./DecisionTool.type";
import type { DatasetImage } from "@/entities/dataset";

import { Flex, Select, Spin } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import GuideLines from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/DefectSuggestions/DecisionTool/DecisionTool/GuideLines";
import HideButton from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/DefectSuggestions/DecisionTool/DecisionTool/HideButton";
import { getDefectSaliencyMap } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/DefectSuggestions/DecisionTool/getDefectSaliencyMap";
import PhotoNavigator from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/DefectSuggestions/DecisionTool/PhotoNavigator/PhotoNavigator";
import { useIsFormEnginePreviewMode } from "@/components/FormEngine/hooks/useIsFormEnginePreviewMode";

import "./DecisionTool.style.less";

const DecisionTool = ({
  selectedMaterial,
  visibleDefects,
  datasetsImage = [],
  ...rest
}: DecisionToolProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDefect, setSelectedDefect] = useState<string | null>(null);
  const isPreviewMode = useIsFormEnginePreviewMode();
  const [imageDatasets, setImageDatasets] = useState<DatasetImage[]>([]);
  const [currentImageName, setCurrentImageName] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [saliencyMapUrl, setSaliencyMapUrl] = useState<string | undefined>("");
  const [defects, setDefects] = useState<Defect[]>([]);
  const [showSaliencyMap, setShowSaliencyMap] = useState(true);

  useEffect(() => {
    setDefects(
      visibleDefects
        // Filter out defects without confidence value. We could use defect.suggested here to narrow down the list of defects.
        .filter((defect) => defect.confidence)
        .map((defect) => ({
          value: defect.defect ?? "",
          label: `${defect.name} (${Math.round((defect.confidence ?? 0) * 100)}%)`,
        }))
    );
  }, [visibleDefects]);

  useEffect(() => {
    const init = () => {
      setImageDatasets(datasetsImage);
    };
    init();
  }, [datasetsImage]);

  useEffect(() => {
    if (currentImageIndex < imageDatasets.length) {
      setCurrentImageName(imageDatasets[currentImageIndex].name);
    }
  }, [currentImageIndex, imageDatasets]);

  const onDefectUpdate = (value: string) => {
    setSelectedDefect(value);
    if (imageDatasets.length && !isPreviewMode) {
      setIsLoading(true);
      setShowSaliencyMap(true);
      getDefectSaliencyMap({
        defect: value,
        imageDataset: imageDatasets[currentImageIndex],
        selectedMaterial,
      })
        .then((returned_saliency_map) => {
          setIsLoading(false);
          setSaliencyMapUrl(
            (returned_saliency_map?.saliency_map as string)?.toString() ?? ""
          );
        })
        .catch((error: Error) => {
          setIsLoading(false);
          console.error("Error while fetching saliency map", error);
        });
    }
  };

  const resetView = () => {
    setSaliencyMapUrl(undefined);
    setSelectedDefect(null);
    setIsLoading(false);
    setShowSaliencyMap(true);
  };

  const onPreviousImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
      resetView();
    }
  };
  const onNextImage = () => {
    if (currentImageIndex < imageDatasets.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
      resetView();
    }
  };

  return (
    <div className="decision-tool-container" {...rest}>
      <div className="decision-tool-first-column">
        <div className="first-column-header">
          <Flex justify="space-between" align="flex-start">
            <Select
              className="decision-tool-defect-selector"
              showSearch={true}
              options={defects}
              placeholder={t("form:staticField.defectSuggestion.placeholder")}
              value={selectedDefect}
              onChange={onDefectUpdate}
              disabled={isLoading}
            />
            <HideButton
              onClick={() => setShowSaliencyMap(!showSaliencyMap)}
              disabled={isLoading || !saliencyMapUrl}
              saliencyMapIsVisible={showSaliencyMap}
            />
          </Flex>
        </div>

        <div className="decision-tool-photo-container">
          {!imageDatasets || !imageDatasets[currentImageIndex] ? (
            <Spin />
          ) : (
            <div className="decision-tool-photo-inner-div">
              <img
                className="decision-tool-photo"
                src={imageDatasets[currentImageIndex].data_path}
                style={{
                  opacity: isLoading ? 0.1 : 1,
                }}
              />
              {isLoading ? (
                <Spin className="decision-tool-saliency-map-spinner" />
              ) : (
                showSaliencyMap &&
                saliencyMapUrl && (
                  <img
                    className="decision-tool-saliency-map"
                    src={saliencyMapUrl}
                  />
                )
              )}
            </div>
          )}
        </div>
        <PhotoNavigator
          isLoading={isLoading}
          currentImageIndex={currentImageIndex}
          currentImageName={currentImageName}
          imageTotalCount={imageDatasets.length}
          onPreviousImage={onPreviousImage}
          onNextImage={onNextImage}
        />
      </div>
      <div className="divider" aria-hidden="true" />
      <div className="decision-tool-second-column">
        <GuideLines />
      </div>
    </div>
  );
};

export default DecisionTool;
